import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import ExpertsList from './ExpertsList';
import LazyAnimation from './LazyAnimation';

const useStyles = makeStyles(theme => ({
  wrapper: {
    paddingTop: 130,
    paddingBottom: 130,
    position: 'relative',
    backgroundColor: '#F7F7F7',
    backgroundImage:
      'linear-gradient(180deg,rgba(12,113,195,0.71) 0%,rgba(12,113,195,0.71) 100%)!important',
  },
  parallaxBg: {
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundImage:
      'url(https://solutions.crediblemind.com/wp-content/uploads/2019/12/editors-2-copy.png)',
    backgroundSize: 'cover',
    backgroundAttachment: 'fixed',
  },
  container: {
    width: '89%',
    maxWidth: '89%',
    padding: '27px 0',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  emptyColumn: {
    width: '30%',
    textAlign: 'center',
    zIndex: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  column: {
    width: '30%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: 30,
    },
  },
  columnBackground: {
    textAlign: 'center',
    zIndex: 0,
    backgroundColor: 'rgba(0,0,0,0.49)',
    padding: 10,
  },
  columnHeader: {
    fontFamily: 'PoppinsSemiBold',
    fontWeight: '600',
    fontSize: 23,
    lineHeight: '1.5em',
    paddingBottom: 25,
    color: '#FFFFFF',
    textAlign: 'left',
  },
  columnBody: {
    fontSize: 16,
    lineHeight: '1.8em',
    fontFamily: 'OpenSansRegular',
    color: '#FFFFFF',
    textAlign: 'left',
  },
  columnPersonContainer: {
    '&:first-child': {
      marginBottom: '9.27%',
    },
  },
  columnPersonName: {
    color: '#FFFFFF',
    fontSize: 18,
    paddingBottom: 10,
    lineHeight: '1em',
    fontFamily: 'OpenSansRegular',
    textAlign: 'left',
  },
  columnPersonTitle: {
    color: '#AAAAAA',
    fontSize: 14,
    lineHeight: '1.7em',
    fontFamily: 'OpenSansRegular',
    textAlign: 'left',
  },
  columnPersonImage: {
    display: 'inline',
    float: 'left',
    marginRight: 15,
  },
  columnPersonDescription: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: '1.7em',
    fontFamily: 'OpenSansRegular',
    textAlign: 'left',
  },
  columnPersonSocialLinks: {
    marginTop: 20,
    textAlign: 'left',
  },
  socialLink: {
    fontSize: 26,
    color: '#B2B2B2',
    '&:hover': {
      color: '#2EA3F2',
    },
  },
  imageWrapper: {
    marginTop: '9.27%',
    marginBottom: '9.27%',
  },
  buttonRoot: {
    color: '#FFFFFF',
    borderRadius: 4,
    letterSpacing: 2,
    fontSize: 15,
    fontFamily: 'MontserratSemiBold',
    fontWeight: '600',
    textTransform: 'uppercase',
    backgroundColor: '#0C71C3',
    marginBottom: '9%',
    padding: '12px 1em',
    transition: 'all 300ms ease 0ms',
    position: 'relative',
    boxShadow: '0px 2px 18px 0px rgba(0,0,0,0.3)',
    '& > svg': {
      position: 'absolute',
      opacity: 0,
      marginLeft: '-1em',
      transition: 'all 300ms ease 0ms',
      right: 4,
    },
    '&:hover': {
      paddingRight: '2em',
      paddingLeft: '0.7em',
      borderRadius: 25,
      '& > svg': {
        opacity: 1,
        marginLeft: 0,
      },
    },
  },
  dividerTop: {
    backgroundImage:
      'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDBweCIgdmlld0JveD0iMCAwIDEyODAgMTQwIiBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxnIGZpbGw9IiNmN2Y3ZjciPjxwYXRoIGQ9Ik0xMjgwIDE0MFYwSDBsMTI4MCAxNDB6Ii8+PC9nPjwvc3ZnPg==)',
    backgroundSize: '100% 100px',
    top: 0,
    right: 0,
    left: 0,
    position: 'absolute',
    height: 100,
    zIndex: 1,
    transform: 'rotateY(180deg)',
  },
  dividerBottom: {
    backgroundImage:
      'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDBweCIgdmlld0JveD0iMCAwIDEyODAgMTQwIiBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxnIGZpbGw9IiNmZmZmZmYiPjxwYXRoIGQ9Ik0wIDB2MTQwaDEyODBMMCAweiIvPjwvZz48L3N2Zz4=)',
    backgroundSize: '100% 100px',
    bottom: 0,
    right: 0,
    left: 0,
    position: 'absolute',
    height: 100,
    zIndex: 1,
  },
}));

function ParallaxB({ onPageExit }) {
  const classes = useStyles();

  return (
    <section id="team-section">
      <div className={classes.wrapper}>
        <div className={classes.parallaxBg} />
        <div className={classes.dividerTop} />
        <div className={classes.container}>
          <div className={classes.emptyColumn} />
          <div className={classes.column}>
            <LazyAnimation type="scale-up-80 fade-in">
              <div className={classes.columnBackground}>
                <Typography className={classes.columnHeader}>
                  Driven by Our Top-notch Editorial Team, Clinical Experts, and
                  Users
                </Typography>
                <Typography className={classes.columnBody}>
                  While our machine-learning algorithms and bots continually
                  search for the latest relevant resources, it’s the human touch
                  from our world-class experts that ensure its evidence-based,
                  validated, and trustworthy.
                </Typography>
                <div className={classes.imageWrapper}>
                  <img
                    src="https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=1024%2C314&amp;ssl=1"
                    alt=""
                    title=""
                    srcSet="https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=1024%2C314&amp;ssl=1 1024w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=300%2C92&amp;ssl=1 300w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=768%2C235&amp;ssl=1 768w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=1536%2C470&amp;ssl=1 1536w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=1080%2C331&amp;ssl=1 1080w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=1280%2C392&amp;ssl=1 1280w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=980%2C300&amp;ssl=1 980w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?resize=480%2C147&amp;ssl=1 480w, https://i0.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/rating-3.png?w=1548&amp;ssl=1 1548w"
                    sizes="(max-width: 1024px) 100vw, 1024px"
                    width="277"
                    height="85"
                  />
                </div>
                <Typography className={classes.columnBody}>
                  Add to the mix real-time ratings from both our experts and
                  users to give a truly dynamic experience that’s highly
                  personalized based on needs and preferences.
                </Typography>
              </div>
            </LazyAnimation>
          </div>
          <div className={classes.column}>
            <LazyAnimation type="scale-up-80 fade-in">
              <div className={classes.columnBackground}>
                <div className={classes.columnPersonContainer}>
                  <Typography className={classes.columnPersonName}>
                    Jonathan Adler, MD
                  </Typography>
                  <Typography className={classes.columnPersonTitle}>
                    Co-editor-in-chief
                  </Typography>
                  <img
                    src="https://i2.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/jon.png?resize=82%2C82&ssl=1"
                    alt="Jonathan Adler, MD"
                    className={classes.columnPersonImage}
                  />
                  <Typography className={classes.columnPersonDescription}>
                    A dedicated clinician and award-winning teacher at
                    Massachusetts General Hospital and Harvard Medical School
                    for 22 years, Jonathan has served as editor of The New
                    England Journal of Medicine, NEJM Journal Watch,
                    eMedicine.com (now the Medscape Reference by WebMD) and has
                    authored or edited 14 books.
                  </Typography>
                  <div className={classes.columnPersonSocialLinks}>
                    <a
                      href="https://www.linkedin.com/in/jonathanadlermd/"
                      target="_blank"
                      onClick={() =>
                        onPageExit(
                          'https://www.linkedin.com/in/jonathanadlermd/',
                          'team-section',
                        )
                      }
                    >
                      <LinkedInIcon className={classes.socialLink} />
                    </a>
                  </div>
                </div>
                <div className={classes.columnPersonContainer}>
                  <Typography className={classes.columnPersonName}>
                    Dustin DiPerna
                  </Typography>
                  <Typography className={classes.columnPersonTitle}>
                    Co-editor-in-chief
                  </Typography>
                  <img
                    src="https://i1.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/dustin-2.png?resize=83%2C83&ssl=1"
                    alt="Dustin DiPerna"
                    className={classes.columnPersonImage}
                  />
                  <Typography className={classes.columnPersonDescription}>
                    Dustin received his training at Cornell University and
                    Harvard University. He is a recognized expert in world
                    religions, positive psychology, and spirituality. Author and
                    editor of four books, he currently teaches at Stanford
                    University.
                  </Typography>
                  <div className={classes.columnPersonSocialLinks}>
                    <a
                      href="https://www.linkedin.com/in/dustindiperna/"
                      target="_blank"
                      onClick={() =>
                        onPageExit(
                          'https://www.linkedin.com/in/dustindiperna/',
                          'team-section',
                        )
                      }
                    >
                      <LinkedInIcon className={classes.socialLink} />
                    </a>
                  </div>
                </div>
              </div>
            </LazyAnimation>
          </div>
        </div>
        <ExpertsList />
        <div className={classes.dividerBottom} />
      </div>
    </section>
  );
}

ParallaxB.propTypes = {
  onPageExit: PropTypes.func,
};

export default ParallaxB;
